import { useMemo } from 'react';
import { MessageExtend, MessageExtendProps } from '.';
import { getMetaFromSender } from '../../utils';
import LazyOrderMessage from '../LazyOrderMessage';
import LazyItemMessage from '../LazyItemMessage';
import styled from '@emotion/styled';
import { MessageStatusDecorate } from '../MessageStatusDecorate';
import { useChatContext } from '../../contexts/ChatContext';
import { isNil } from 'lodash-es';

export const MessageExtendComplex = (props: MessageExtendProps) => {
  const { raw, msg, conversation, content, showSourceContent = true } = props;

  const { dialogRef, showSourceContent: showSourceContentContext } = useChatContext();
  const { buyerId, storeId, tunnelAccountId } = conversation;
  const { sourceContent, failCause } = raw;
  const { sender } = msg;

  const { layout } = useMemo(() => {
    return getMetaFromSender(sender);
  }, [sender]);

  const sourceContentEnabled = useMemo(() => {
    if (!isNil(showSourceContentContext)) {
      return showSourceContentContext;
    }

    return showSourceContent;
  }, [showSourceContentContext, showSourceContent]);

  const wrapStatusContent = (
    <MessageStatusDecorate
      message={raw}
      status={msg?.status}
      content={content}
      failCause={failCause}
      dialogRef={dialogRef}
    />
  );

  if (
    sourceContentEnabled &&
    sourceContent?.sourceContentType === 'ORDER' &&
    sourceContent?.externalOrderSn
  ) {
    return (
      <LazyOrderMessage
        rawMsg={raw}
        buyerId={buyerId}
        storeId={storeId}
        tunnelAccountId={tunnelAccountId ?? '0'}
        externalTradeId={`${sourceContent?.externalOrderSn}`}
        externalStoreId={sourceContent?.externalStoreId ?? ''}
        lazyScrollContainer={dialogRef?.dom}
      >
        {(component, status) => {
          return (
            <MessageExtend
              raw={raw}
              msg={msg}
              conversation={conversation}
              content={<ExtraMsgContent layout={layout}>{wrapStatusContent}</ExtraMsgContent>}
              card={status !== 'success' ? undefined : component}
            />
          );
        }}
      </LazyOrderMessage>
    );
  }
  if (
    sourceContentEnabled &&
    raw.sourceContent?.sourceContentType === 'ITEM' &&
    raw.sourceContent?.externalItemId
  ) {
    return (
      <LazyItemMessage
        rawMsg={raw}
        storeId={conversation?.storeId || ''}
        externalItemId={`${raw.sourceContent?.externalItemId}`}
        externalStoreId={raw.sourceContent.externalStoreId || ''}
        lazyScrollContainer={dialogRef?.dom}
      >
        {(component, status) => {
          return (
            <>
              <MessageExtend
                raw={raw}
                msg={msg}
                conversation={conversation}
                content={<ExtraMsgContent layout={layout}>{wrapStatusContent}</ExtraMsgContent>}
                card={status !== 'success' ? undefined : component}
              />
            </>
          );
        }}
      </LazyItemMessage>
    );
  }

  return <MessageExtend {...props} content={wrapStatusContent} />;
};

const ExtraMsgContent = styled.div<{ layout: 'left' | 'right' }>`
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ layout }) => (layout === 'right' ? 'flex-end' : 'flex-start')};
`;
